import React, { useEffect } from "react";
import { ArrowLeft, BookOpen, Check, Coins, X } from "lucide-react";
import { ONBOARDING_CREDITS, ONBOARDING_STEP_CREDITS } from "@fastn/core";
import { cn } from "@/lib/utils";
import { FastnMark } from "./icons";
import { onboardingTestIds as tid } from "@/testids/onboardingTestIds";
import "./onboarding.css";

export { FastnMark };

/** The four steps of the first integration, as the header pill and Home name them. */
export const STEP_TITLES = [
  "Connect your product",
  "Choose integrations",
  "Embed the widget",
  "Customers connect and customize",
] as const;

/** What each step earns: the same numbers the api grants (ai_credit_grants). */
export const STEP_CREDITS = ONBOARDING_STEP_CREDITS;
export const SETUP_CREDITS = ONBOARDING_CREDITS;

type Scope = "ob3" | "ob3e1";

/**
 * The artboard. The scope class sits on a wrapper outside `.ds` so the design's
 * own `.ds .x` selectors keep matching; see onboarding.css.
 */
export function Frame({
  scope = "ob3",
  className,
  children,
  testId,
}: {
  scope?: Scope;
  className?: string;
  children: React.ReactNode;
  testId?: string;
}): React.ReactElement {
  return (
    <div className={scope} data-testid={testId}>
      <div className={cn("ds obr [box-sizing:border-box]! flex! flex-col! h-dvh! w-full! [padding:28px_40px]! max-sm:[padding:20px_16px]! relative! overflow-hidden!", className)}>
        {children}
      </div>
    </div>
  );
}

export function Brand(): React.ReactElement {
  return (
    <>
      <span className="wsMark"><FastnMark height={22} /></span>
      <span className="[font-size:var(--text-body-size-large)]! [font-weight:var(--base-text-weight-semibold)]! [letter-spacing:-.014em]!">fastn</span>
    </>
  );
}

export type CreditsBadge =
  | { kind: "get" }
  | { kind: "earned"; n: number }
  | { kind: "added"; n: number };

/** A step whose credits the server just granted, celebrated under the pill. */
export interface CreditsToast {
  step: 1 | 2 | 3 | 4;
  credits: number;
}

function CreditsPill({ credits, toast }: { credits: CreditsBadge; toast?: CreditsToast | null }): React.ReactElement {
  return (
    <span className="pillw">
    <span
      className="badge max-md:hidden! h-[30px]! [padding:0_10px]! gap-[6px]! [border-radius:var(--borderRadius-medium)]!"
      title="Finish onboarding to get 50 AI credits. Use them to connect your product and build your integrations."
      data-testid={tid.creditsBadge}
    >
      <Coins className="i xs" aria-hidden="true" />
      {credits.kind === "get" && (
        <><span className="hidden! xl:inline!">Finish setup, get </span><b className="[color:var(--fgColor-default)]!">{SETUP_CREDITS}</b> AI credits</>
      )}
      {credits.kind === "earned" && (
        <><b className="[color:var(--fgColor-default)]!">{credits.n}</b> of {SETUP_CREDITS}<span className="hidden! xl:inline!"> AI credits earned</span></>
      )}
      {credits.kind === "added" && (
        <><b className="[color:var(--fgColor-success)]!">+{credits.n}</b> AI credits added</>
      )}
    </span>
    {toast && (
      <div key={toast.step} className="stoast" role="status" data-testid={tid.creditsToast}>
        <span className="stck"><Check className="i sm" aria-hidden="true" /><i /><i /><i /><i /></span>
        <span>
          <div className="stt">Step {toast.step} done! Nice work.</div>
          <div className="sts">+{toast.credits} AI credits added to your account</div>
        </span>
      </div>
    )}
    </span>
  );
}

export function StepPill({ step }: { step: 1 | 2 | 3 | 4 }): React.ReactElement {
  return (
    <div
      className="flex! items-center! gap-[12px]! max-sm:gap-[8px]! h-[32px]! max-sm:h-[28px]! [padding:0_16px]! max-sm:[padding:0_10px]! [border:1px_solid_var(--borderColor-default)]! [border-radius:var(--borderRadius-full)]! [background:var(--bgColor-muted)]! whitespace-nowrap!"
      title={STEP_TITLES.join(" › ")}
      data-testid={tid.stepPill}
    >
      <span className="t12 sub">Step {step} of 4</span>
      <span className="flex! max-[420px]:hidden! gap-[3px]!">
        {[1, 2, 3, 4].map((n) => (
          <span
            key={n}
            className={cn(
              "w-[14px]! h-[4px]! [border-radius:2px]!",
              n <= step ? "[background:var(--fgColor-default)]!" : "[background:var(--borderColor-default)]!",
            )}
          />
        ))}
      </span>
      <span className="hidden! lg:inline! t12 [font-weight:var(--base-text-weight-semibold)]!">{STEP_TITLES[step - 1]}</span>
    </div>
  );
}

/**
 * The top bar every journey screen shares: mark, credits, the step pill, the
 * AI-tool pop-up and Skip for now. Each piece is optional because the design
 * drops some on some screens (no pill before step 1, no skip on the welcome).
 */
export function Header({
  credits,
  toast,
  step,
  aiTools,
  onSkip,
  skipLabel = "Skip for now",
}: {
  credits?: CreditsBadge;
  toast?: CreditsToast | null;
  step?: 1 | 2 | 3 | 4;
  aiTools?: React.ReactNode;
  onSkip?: () => void;
  skipLabel?: string;
}): React.ReactElement {
  // Three columns with equal outer tracks, so the step pill sits at the page's
  // centre at every width whatever the two sides hold.
  return (
    <div className="grid! grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]! items-center! gap-[10px]! [flex:0_0_auto]!">
      <div className="flex! items-center! gap-[10px]! min-w-0!">
        <Brand />
        {credits && (
          <>
            <span className="w-[10px]! max-md:hidden!" />
            <CreditsPill credits={credits} toast={toast} />
          </>
        )}
      </div>
      <div>{step && <StepPill step={step} />}</div>
      <div className="flex! items-center! justify-end! gap-[8px]! min-w-0!">
        {aiTools}
        {onSkip && (
          <button type="button" className="btn btn--ghost btn--sm cursor-pointer!" onClick={onSkip} data-testid={tid.skip}>
            <span className="max-sm:hidden!">{skipLabel}</span>
            <span className="sm:hidden!">Skip</span>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * A screen's scrolling content area. Centred with auto margins rather than
 * "safe center", which still clipped the top: the margins collapse to 0 when
 * the content is taller than the window, so the whole screen scrolls. The top
 * padding keeps the content off the header when it does.
 */
export const WRAP = "wrap pt-[16px]! [&>:first-child]:mt-auto! [&>:last-child]:mb-auto!";

/** A screen's link to the docs for what it asks, on the right of its action row. */
export function DocsLink({ href, children, testId, className }: { href: string; children: React.ReactNode; testId: string; className?: string }): React.ReactElement {
  return (
    <a className={cn("lnk t12 inline-flex! items-center! gap-[4px]! whitespace-nowrap! max-sm:basis-full!", className)} href={href} target="_blank" rel="noreferrer" data-testid={testId}>
      <BookOpen className="i xs" aria-hidden="true" /> {children}
    </a>
  );
}

export function BackButton({ onClick, label = "Back" }: { onClick: () => void; label?: string }): React.ReactElement {
  return (
    <button
      type="button"
      className="btn btn--ghost btn--sm self-start! [margin:0_0_18px_-10px]! cursor-pointer!"
      onClick={onClick}
      data-testid={tid.back}
    >
      <ArrowLeft className="i xs" aria-hidden="true" /> {label}
    </button>
  );
}

/** The square initial the design uses for the product before it has a logo. */
export function ProductMark({ name, className }: { name: string; className?: string }): React.ReactElement {
  return (
    <span className={cn("hm", className)} aria-hidden="true">
      {(name.trim()[0] ?? "?").toUpperCase()}
    </span>
  );
}

/**
 * A pop-up card on its own (not anchored to a header button): the design's
 * `.cpop-card` over its backdrop, with the same close button.
 */
export function Overlay({
  label,
  onClose,
  children,
  testId,
  raised = false,
}: {
  label: string;
  onClose: () => void;
  children: React.ReactNode;
  testId?: string;
  /** Above another open pop-up, as the design's nested "Finish setup" card is. */
  raised?: boolean;
}): React.ReactElement {
  return (
    <>
      <div className={cn("cpop-bg", raised && "z-[42]!")} aria-hidden="true" onClick={onClose} /> {/* design-tokens-ignore: the scrim; the Close button below is the keyboard path */}
      <button
        type="button"
        className={cn("ob3-pop-x cursor-pointer!", raised && "z-[45]!")}
        aria-label="Close"
        onClick={onClose}
        data-testid={tid.popupClose}
      >
        <X className="i sm" aria-hidden="true" />
      </button>
      <div className={cn("cpop-card", raised && "z-[43]!")} role="dialog" aria-label={label} data-testid={testId}>
        {children}
      </div>
    </>
  );
}

/** The heading row every pop-up card opens with; the spacer keeps the title clear of the close button. */
export function PopTitle({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="flex! items-center! gap-[10px]!">
      <span className="b flex-1! ob3-fs18">{children}</span>
      <span className="w-[28px]! h-[28px]! [flex:0_0_28px]!" />
    </div>
  );
}

/**
 * What the agent asks to be checked, as a dialog over the build. It was a
 * drawer on the left, as the agent page opens its panels beside the chat, but
 * without a chat beside it the agent's own panels (an iframe of a fastn page)
 * kept half their width and left the rest of the screen blank.
 */
export function Sheet({ label, icon, testId, closeTestId, onClose, size, children }: {
  label: string;
  icon?: React.ReactNode;
  testId: string;
  closeTestId?: string;
  onClose: () => void;
  /** sm: sized to its content; md: a fixed medium frame, for a live preview. */
  size?: "sm" | "md";
  children: React.ReactNode;
}): React.ReactElement {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="ob3-sheet-scrim">
      <button type="button" className="ob3-sheet-backdrop" tabIndex={-1} aria-label="Close" onClick={onClose} />
      <div className={size ? `ob3-sheet ob3-sheet--${size}` : "ob3-sheet"} role="dialog" aria-modal="true" aria-label={label} data-testid={testId}>
        {closeTestId !== undefined && (
          <div className="ob3-sheet-hd">
            {icon}
            <span className="b flex-1!">{label}</span>
            <button type="button" className="btn btn--ghost btn--icon btn--sm cursor-pointer!" onClick={onClose} aria-label="Close" data-testid={closeTestId}><X className="i sm" aria-hidden="true" /></button>
          </div>
        )}
        <div className={closeTestId !== undefined ? "ob3-sheet-bd" : "ob3-sheet-raw"}>{children}</div>
      </div>
    </div>
  );
}
